/**
 * PR 6b — Phase 1 — Drift API service module.
 *
 * Module-level functions (ADR-6b §A1) that wrap the OMS drift endpoints
 * exposed via api-gw under `/oms/admin/orders/:id/proposal/{drift,
 * refresh-drift, resolve-drift}`. All functions:
 *
 *   - Take the axios singleton from `hooks/api.tsx` (Accept-Version is
 *     injected by the interceptor, callers don't pass it).
 *   - Return typed payloads from `model/Proposal.ts` on success.
 *   - Reject with a typed `ApiError` (helpers/apiErrors.ts) on failure —
 *     never a raw AxiosError, so UI switch statements stay exhaustive.
 *
 * Naming convention: `*Api.ts` for service modules in `helpers/` (matches
 * `proposalApi.ts`). Not React-aware — usable from any layer.
 *
 * Backend routes (OMS Go, `cmd/server/main.go:266-268`):
 *   GET    /admin/orders/:id/proposal/drift          → GetProposalDrift
 *   POST   /admin/orders/:id/proposal/refresh-drift  → RefreshProposalDrift
 *   POST   /admin/orders/:id/proposal/resolve-drift  → ResolveProposalDrift
 *
 * The `X-Bulk-Accept-All: true` header on resolve is the wire contract
 * (NOT a body field); we expose it as a parameter so callers don't have
 * to remember the header name.
 *
 * See ADR-6b §C1 (optimistic toggle + revert) and §E1 (Alert inline
 * feedback) for UI-side semantics.
 */

import api from "../hooks/api";
import { ApiError, toApiError } from "./apiErrors";
import type {
  DriftReport,
  ResolveDriftRequest,
  ResolveDriftResponse,
} from "../model/Proposal";

const OMS_ORDERS_PREFIX = "/oms/admin/orders";

/** GET the current drift report for the latest revision of an order.
 *  Empty `items: []` means no drift. */
export async function fetchDrift(orderId: string): Promise<DriftReport> {
  try {
    const res = await api.get<DriftReport>(
      `${OMS_ORDERS_PREFIX}/${encodeURIComponent(orderId)}/proposal/drift`,
    );
    return res.data;
  } catch (err) {
    throw toApiError(err);
  }
}

/** POST to force a re-evaluation of drift (e.g. after upstream price changes
 *  in Niposom). Returns the freshly computed report. */
export async function refreshDrift(orderId: string): Promise<DriftReport> {
  try {
    const res = await api.post<DriftReport>(
      `${OMS_ORDERS_PREFIX}/${encodeURIComponent(orderId)}/proposal/refresh-drift`,
    );
    return res.data;
  } catch (err) {
    throw toApiError(err);
  }
}

/** Options for `resolveDrift`. `bulkAcceptAll` translates to the
 *  `X-Bulk-Accept-All: true` request header (NOT a body field — see
 *  Proposal.ts ResolveDriftRequest comment). */
export interface ResolveDriftOptions {
  bulkAcceptAll?: boolean;
}

/** POST a resolve-drift action. Throws `ApiError` on failure.
 *  When `opts.bulkAcceptAll` is true, sets `X-Bulk-Accept-All: true`. */
export async function resolveDrift(
  orderId: string,
  body: ResolveDriftRequest,
  opts: ResolveDriftOptions = {},
): Promise<ResolveDriftResponse> {
  try {
    const headers: Record<string, string> = {};
    if (opts.bulkAcceptAll) {
      headers["X-Bulk-Accept-All"] = "true";
    }
    const res = await api.post<ResolveDriftResponse>(
      `${OMS_ORDERS_PREFIX}/${encodeURIComponent(orderId)}/proposal/resolve-drift`,
      body,
      { headers },
    );
    return res.data;
  } catch (err) {
    throw toApiError(err);
  }
}

/** Type-guard helper for try/catch sites that want to narrow without
 *  importing ApiError directly. */
export function isApiError(value: unknown): value is ApiError {
  return (
    typeof value === "object" &&
    value !== null &&
    "kind" in value &&
    "raw" in value
  );
}
